/**
 * Relatório de prospecção do LeadExplorer → blocos para a aba "Estratégia de prospecção".
 *
 * O backend pede à IA um HTML só com <p> e <h3> (src/app/app.js, reportGenerate; o dossiê com IA
 * em src/app/v2/lib/leadExplorerDossier.js): um parágrafo
 * de resumo e seções "<h3>🔥 Dores comuns desse mercado</h3><p>- item</p>…". Aqui viram texto
 * puro (nada do HTML da IA vai para a página) e cada seção ganha ícone e cor pelo emoji.
 */

export interface ReportSection {
  title: string;
  icon: string;
  tone: 'red' | 'gold' | 'blue' | 'green' | 'purple' | 'gray';
  items: string[];
  paragraphs: string[];
}

export interface ParsedReport {
  intro: string[];
  sections: ReportSection[];
}

const EMOJI_STYLE: [RegExp, string, ReportSection['tone']][] = [
  [/🔥/u, 'local_fire_department', 'red'],
  [/🌟|⭐/u, 'star', 'gold'],
  [/🚀/u, 'rocket_launch', 'blue'],
  [/🚨/u, 'campaign', 'red'],
  [/📅|🗓/u, 'event', 'purple'],
  [/🎯/u, 'target', 'green'],
  [/👥|👤/u, 'groups', 'blue'],
  [/📞|☎/u, 'call', 'green'],
  [/📡/u, 'chat', 'purple'],
  // Tópicos do dossiê com IA (decisão 375): a pesquisa na web entrou no mesmo documento.
  [/🏢|🏬/u, 'apartment', 'gray'],
  [/🧭/u, 'explore', 'gray'],
  [/📊|📈/u, 'monitoring', 'gray'],
  [/💻|🖥/u, 'devices', 'gray'],
  [/📰|🗞/u, 'newspaper', 'gray'],
];

const EMOJI = /^[\p{Extended_Pictographic}\p{Emoji_Presentation}️‍\s]+/u;

function clean(text: string): string {
  return text.replace(/\s+/g, ' ').trim();
}

function styleOf(raw: string): Pick<ReportSection, 'icon' | 'tone'> {
  for (const [re, icon, tone] of EMOJI_STYLE) if (re.test(raw)) return { icon, tone };
  return { icon: 'tips_and_updates', tone: 'gray' };
}

/** Separa resumo e seções. Texto sem <h3> vira só resumo. */
export function parseReport(html: string | undefined): ParsedReport {
  const out: ParsedReport = { intro: [], sections: [] };
  if (!html) return out;
  const doc = new DOMParser().parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return out;

  let current: ReportSection | null = null;
  const blocks = Array.from(root.childNodes);
  for (const node of blocks) {
    const tag = node.nodeType === 1 ? (node as Element).tagName.toLowerCase() : '#text';
    if (tag === 'script' || tag === 'style' || tag === 'template' || tag === 'noscript') continue;
    const text = clean(node.textContent ?? '');
    if (!text) continue;
    if (/^h[1-6]$/.test(tag)) {
      current = {
        title: clean(text.replace(EMOJI, '')),
        ...styleOf(text),
        items: [],
        paragraphs: [],
      };
      out.sections.push(current);
      continue;
    }
    if (tag === 'ul' || tag === 'ol') {
      const items = Array.from((node as Element).querySelectorAll('li')).map((li) =>
        clean(li.textContent ?? ''),
      );
      if (current) current.items.push(...items.filter(Boolean));
      else out.intro.push(...items.filter(Boolean));
      continue;
    }
    const item = text.match(/^[-–•*]\s*(.+)$/);
    if (current) {
      if (item) current.items.push(item[1]!);
      else current.paragraphs.push(text);
    } else {
      out.intro.push(item ? item[1]! : text);
    }
  }
  return out;
}

/** Texto para copiar: resumo, títulos e itens com hífen. */
export function reportToText(report: ParsedReport): string {
  const parts: string[] = [...report.intro];
  for (const section of report.sections) {
    parts.push('', section.title);
    parts.push(...section.paragraphs);
    parts.push(...section.items.map((item) => `- ${item}`));
  }
  return parts.join('\n').trim();
}
